import { RenderEngine } from "./RenderEngine";
import { Mesh, MeshBasicMaterial, MeshPhysicalMaterial, NormalBlending, Vector2, ShaderMaterial, Material } from "three";
import { getXRShadowMaterial } from "../ARController/XRShadowMaterial";
import { AssetInfoTypes } from "threngine";
import { IRenderService } from "threngine/dist/types/src/core/render/IRenderService";
import { ModelLoadController } from "./ModelLoad/ModelLoadController";

export class CaptureController {
  private renderService:IRenderService;

  public constructor (
    private engine:RenderEngine,
    private modelLoadController:ModelLoadController
  ) {
    this.renderService = this.engine.engine.services.render;

    (window as any)["capture"] = this;
  }

  public async screenshot(landscape:boolean):Promise<void> {
    const scene = this.engine.scene;
    const configurator = this.engine.configurator;
    const assetProvider = this.engine.assetProvider;
    const camName = landscape ? "GHS Doors Left" : "Orbit Mobile";
    const qualityPresetName = "Model Switch Screenshot";
    const hiddenObjects = [];

    let shadowPlane = <Mesh>scene.getObjectByName("Shadow#Ground_Shadow");
    let shadowOriginalMaterial:MeshBasicMaterial;
    if (shadowPlane) {
      shadowOriginalMaterial = <MeshBasicMaterial>shadowPlane.material;
      if (shadowOriginalMaterial) {
        shadowPlane.material = getXRShadowMaterial(shadowOriginalMaterial.map);
      }
    }

    
    const qualityPreset = assetProvider.getAssets().find(
      a => a.info.name === qualityPresetName && a.infoType === AssetInfoTypes.Settings);
    this.engine.engine.services.render.adaptiveQualityController.enabled = false;
    this.engine.changeRenderSetting(qualityPreset.id);
    
    await configurator.setCamera(
      assetProvider.getAssets().find(a => a.info.name === camName).id);

    this.engine.camera.zoom = landscape ? 1 : 1.7;

    // 1. Hide skydome and make background transparent
    // 2. Fix glass on transparent background
    scene.traverse(c => {
      if (c.name.includes("Skydome") && c.visible) {
        c.visible = false;
        hiddenObjects.push(c);
      }

      if (c instanceof Mesh &&
          Array.isArray(c.material) &&
         (c.name.includes("Window")) &&
          c.material[1] instanceof MeshPhysicalMaterial
      ) {
        c.material = createGlassReplacementMaterial(c.material, 0.6);
      }
    });
    scene.background = null;

    const renderController = this.renderService.renderController;
    const originalSize = new Vector2();
    renderController.renderer.getSize(originalSize);
    const width = 4096 / window.devicePixelRatio;
    const height = 4096 * (landscape ? (9 / 16) : 1) / window.devicePixelRatio;
    renderController.resize(width, height);

    let framesToSample = 64;

    const onAfterRender = async () => {
      if (--framesToSample > 0) {
        return;
      }

      renderController.renderer.domElement.toBlob((blob:Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.modelLoadController.getModel().car}${landscape ? '-side' : ''}.png`;
        link.click();

        // We're done, restore original state.
        // TODO: restore original glass materials
        for (const object of hiddenObjects) {
          object.visible = true;
        }
        shadowPlane.material = shadowOriginalMaterial;
        renderController.resize(originalSize.width, originalSize.height);
      }, "image/png");

      this.renderService.onAfterRender.unsubsribe(onAfterRender);
    };

    this.renderService.onAfterRender.subscribe(onAfterRender);
  }
}

export function createGlassReplacementMaterial(material:Material[], opacity?:number):Material[] {
  const multiply = material[0] as ShaderMaterial;
  const pbr = material[1] as MeshPhysicalMaterial;
  
  const color = multiply.uniforms.diffuse.value;
  
  if (opacity === undefined) {
    opacity = 1 - ((color.x + color.y + color.z) / 3);
  }

  const darkenMaterial = new MeshPhysicalMaterial({
    color: 0x000000,
    blending: NormalBlending,
    opacity: opacity,
    transparent: true,
    roughness: 0,
    metalness: 0,
    depthWrite: false,
  });

  const reflectionMaterial = new ShaderMaterial({
    uniforms: {
      envMap: {
        value: pbr.envMap
      },
      opacity: {
        value: 0.3
      }
    },
    blending: NormalBlending,
    transparent: true,
    vertexShader: `
      varying vec3 v_normal;
      varying vec3 v_view_position;

      void main() {
        v_normal = normalMatrix * normal;
        v_view_position = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      #define ENVMAP_TYPE_CUBE_UV

      vec4 envMapTexelToLinear(vec4 color) {
        return vec4(color.rgb * exp2((color.a * 255.0) - 128.0), 1.0);
      }

      #include <common>
      #include <cube_uv_reflection_fragment>

      varying vec3 v_normal;
      varying vec3 v_view_position;

      uniform sampler2D envMap;
      uniform float opacity;

      void main() {
        vec3 normal = normalize(v_normal);

        vec3 view_dir = normalize(v_view_position);
        vec3 reflect_dir = reflect(view_dir, normal);
        reflect_dir = inverseTransformDirection(reflect_dir, viewMatrix);

        float fresnel = 0.04 + 0.96 * pow(1.0 - max(0.0, dot(-view_dir, normal)), 5.0);
        vec4 reflection = textureCubeUV(envMap, reflect_dir, 0.0125) * fresnel;
        float luma = dot(reflection.rgb, vec3(0.2126, 0.7152, 0.0722));
        luma = opacity + luma * (1.0 - opacity);

        gl_FragColor = vec4(reflection.rgb, luma);
      }
    `
  });

  return [darkenMaterial, reflectionMaterial];
}